import { useState, useMemo, useRef, useEffect } from "react";
import type { ReactNode, ChangeEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  INTERVIEW_LANGUAGES,
  type InterviewLanguageCode,
  type Ownership,
  type SalaryCurrency,
} from "@talenthop/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api.js";
import { Sparkles, Briefcase, UploadCloud, RefreshCw } from "../../components/icons.js";

import { useMyOrg } from "./useMyOrg.js";
import { useOrgPlans } from "./planUsage.js";
import { useVerticals } from "./useVerticals.js";
import {
  parseDynamicPrompt,
  looksLikeJobDescription,
  namesKnownCity,
  mergeAiSpec,
  extractExperience,
  type AiParsedJd,
  type ParsedRoleSpec,
  type CompanyContext,
} from "./promptAnalyzer.js";
import { suggestVerticalKey } from "./verticalSuggest.js";
import { SearchableSelect, DEPARTMENT_OPTIONS, INDUSTRY_OPTIONS } from "../../components/SearchableSelect.js";
import {
  BudgetFields,
  CheckedSkillChips,
  COUNTRY_CURRENCY,
  ExperienceInput,
  LocationFields,
  amountOf,
  budgetFromConfig,
  budgetProblem,
  budgetToConfig,
  joinLocation,
  placeOnly as placeOf,
  splitLocation,
  useSkillChecks,
  workModeLabel,
  type Budget,
} from "./specFields.js";
import { CompetencyPicker } from "../../components/CompetencyPicker.js";
import { Dropdown } from "../../components/Dropdown.js";
import { MarkdownEditor } from "../../components/MarkdownEditor.js";

const DEFAULT_PROMPT =
  "Requirement for a Senior Full Stack Engineer (Node.js & Next.js) with 5+ years building high-concurrency payment APIs.";

const DEFAULT_SPEC: ParsedRoleSpec = parseDynamicPrompt(DEFAULT_PROMPT);

/** "Chennai, India (Hybrid)" → "Chennai, India". The spec's location is the place; mode lives in spec.mode. */
const placeOnly = (loc: string): string => placeOf(loc) || "Bangalore, India";

/**
 * Examples. The first three are full job descriptions (Markdown: about the company, the role,
 * responsibilities, requirements) so a click shows what a complete brief looks like — they land in
 * the box, and on Review & save the whole JD becomes the editable Role summary. `company` is the
 * org's name when known.
 */
const PRESET_PROMPTS: { label: string; text: (company: string) => string }[] = [
  {
    label: "I need a Senior Manual QA Tester",
    text: (company) => `Senior Manual QA Tester

### About the company
${company} builds web and mobile products used by thousands of customers every day. Our engineering teams ship every week, and quality is everyone's job — QA sits inside each product squad, not at the end of the line.

### About the role
We are hiring a Senior Manual QA Tester with 5+ years of experience to own the quality of our customer-facing web and mobile apps. You will plan and run testing for new features, catch issues before our customers do, and help the team raise the bar on release quality.

Location: Bangalore, India (Hybrid)
Experience: 5+ years

### Roles & responsibilities
- Read product requirements and designs, and turn them into clear test plans, test scenarios and test cases
- Run functional, regression, integration, smoke and exploratory testing on web (Chrome, Safari, Edge) and mobile (Android, iOS)
- Log defects in Jira with clear steps, screenshots and severity, and follow them through to closure
- Test REST APIs with Postman, and check data in the database with basic SQL queries
- Own the regression suite for your squad and decide what must pass before a release
- Take part in sprint planning, stand-ups and release sign-off with developers and product managers
- Mentor junior testers and review their test cases

### Requirements
- 5+ years of manual software testing on web and/or mobile products
- Strong knowledge of STLC, SDLC and Agile/Scrum ways of working
- Hands-on with Jira (or a similar tracker), TestRail or Zephyr, and Postman
- Working knowledge of SQL for data checks
- Clear written and spoken English; able to explain a bug to a developer and a risk to a manager

### Nice to have
- ISTQB certification
- Exposure to test automation (Selenium, Playwright or Appium) or interest in learning it
- Experience testing payments, accessibility or performance`,
  },
  {
    label: "I need a Frontend Engineer",
    text: (company) => `Frontend Engineer

### About the company
${company} builds modern web products with a small, senior engineering team. We care about fast, accessible interfaces and about code that the next engineer can read.

### About the role
We are looking for a Frontend Engineer with 3+ years of experience to build the screens our customers use every day. You will work with design and backend engineers to take features from a Figma file to production.

Location: Bangalore, India (Hybrid)
Experience: 3+ years

### Roles & responsibilities
- Build responsive, accessible user interfaces with React and TypeScript
- Turn Figma designs into reusable components and keep our design system consistent
- Connect the UI to REST and GraphQL APIs, and handle loading, error and empty states properly
- Write unit and component tests (Jest, React Testing Library) and fix what they find
- Improve page speed and Core Web Vitals; profile and remove slow renders
- Review pull requests and help keep the frontend codebase clean
- Work closely with product and design in sprint planning and demos

### Requirements
- 3+ years building production web apps with React
- Strong JavaScript and TypeScript, HTML5 and modern CSS (Flexbox, Grid)
- Experience with state management (React Query, Redux or Zustand)
- Comfortable with Git, code review and CI pipelines
- Understanding of web accessibility (WCAG) and cross-browser behaviour

### Nice to have
- Next.js and server-side rendering
- Tailwind CSS or CSS-in-JS
- Playwright or Cypress end-to-end tests`,
  },
  {
    label: "I need an Automation Test Engineer",
    text: (company) => `Automation Test Engineer

### About the company
${company} ships software to customers every week, and we rely on fast, trustworthy automated tests to do it safely. We are growing our quality engineering team.

### About the role
We need an Automation Test Engineer with 4+ years of experience to design, build and maintain our test automation for web, API and mobile. You will make our release checks faster and more reliable, and help every squad test earlier.

Location: Pune, India (Hybrid)
Experience: 4+ years

### Roles & responsibilities
- Design and build automation frameworks for web UI (Selenium WebDriver or Playwright) and REST APIs (Rest Assured or Postman/Newman)
- Write clean, maintainable automated tests in Java or TypeScript, following the Page Object Model
- Run the automation suite in CI/CD (Jenkins, GitHub Actions or Azure DevOps) on every build and report results
- Find and fix flaky tests; keep the suite fast and trusted
- Work with developers and manual testers to decide what to automate and what to test by hand
- Add test data setup, environment checks and clear failure reports
- Track automation coverage and share it with the team every sprint

### Requirements
- 4+ years in test automation
- Strong programming in Java or TypeScript/JavaScript
- Hands-on with Selenium or Playwright, TestNG/JUnit or Jest, and API testing tools
- Experience running tests in CI/CD pipelines and using Git
- Good understanding of testing types, STLC and Agile

### Nice to have
- Mobile automation with Appium
- Performance testing with JMeter or k6
- Docker and cloud test grids (BrowserStack, Sauce Labs)`,
  },
  {
    label: "Senior Full Stack Engineer (Node.js & Next.js) with 5+ years building payment checkout APIs",
    text: () => "Senior Full Stack Engineer (Node.js & Next.js) with 5+ years building payment checkout APIs",
  },
  ...[
    ".NET Developer Backend (3+ Years) with C#, ASP.NET Core WebAPI and SQL Server",
    "ICU Staff Nurse (Critical Care, 3+ years) in Bangalore with ACLS/BLS certification",
  ].map((p) => ({ label: p, text: () => p })),
];

/** Mode A ("We source") package — the sourcingSchema minimums (packages/shared/src/schemas.ts). */
const SOURCING_SCREENS = 40;
const SOURCING_INTERVIEWS = 20;
const SOURCING_FINALISTS = 3;
/** Per-browser memory of the last We source / I'll source choice. */
const SOURCING_PREF_KEY = "talenthop_sourcing_owner";

/**
 * Two-option segmented control (We source / I'll source, We interview / I'll interview). The
 * selected option's meaning is spelled out underneath, so nobody has to hover to find out.
 */
function OwnershipToggle({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: Ownership;
  onChange: (v: Ownership) => void;
  options: { value: Ownership; label: string; hint: string }[];
}): ReactNode {
  return (
    <div className="nr-ownership">
      <div className="nr-ownership-label">{label}</div>
      <div className="nr-segmented" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            className={`nr-segment${value === o.value ? " on" : ""}`}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="nr-ownership-hint" aria-live="polite">
        {options.find((o) => o.value === value)?.hint}
      </p>
    </div>
  );
}

const SOURCING_OPTIONS: { value: Ownership; label: string; hint: string }[] = [
  {
    value: "us",
    label: "We source",
    hint: `TalentHop finds the candidates for you: we screen ${SOURCING_SCREENS}+ resumes, AI-interview the top ${SOURCING_INTERVIEWS} and recommend ${SOURCING_FINALISTS} finalists. You only review the shortlist.`,
  },
  {
    value: "client",
    label: "I'll source",
    hint: "You bring the candidates: upload CVs, share an apply link or import them. We screen and interview whoever you add.",
  },
];

/** Every way into New requirement lands here, so the job-allowance check sits on the page itself:
 *  with 0 jobs left (on a metered org) the recruiter never enters the workflow only to be refused
 *  at Save — they're sent to Plans & usage, and come back here once a plan is bought. */
export function NewRequest(): ReactNode {
  const plans = useOrgPlans();
  const d = plans.data;
  const outOfJobs = Boolean(d && d.jobsMetered && d.usage.job.remaining <= 0);
  if (plans.isLoading) {
    return (
      <section className="page">
        <div className="card pad mut sm">Checking your plan…</div>
      </section>
    );
  }
  if (outOfJobs) return <NoJobsLeft canBuy={d!.canBuy} />;
  return <NewRequestForm />;
}

function NoJobsLeft({ canBuy }: { canBuy: boolean }): ReactNode {
  return (
    <section className="page">
      <div className="page-h">
        <div>
          <h1>New requirement</h1>
          <div className="sub">Describe the role in plain words. AI drafts the screening criteria and interview plan.</div>
        </div>
      </div>
      <div className="card" style={{ maxWidth: 640 }} role="status">
        <div className="card-b" style={{ display: "grid", gap: 12, justifyItems: "start", padding: 28 }}>
          <span className="mark" aria-hidden="true" style={{ display: "inline-flex" }}>
            <Briefcase />
          </span>
          <h2 style={{ margin: 0, fontSize: 20 }}>You don't have any job credits remaining.</h2>
          <p className="mut" style={{ margin: 0 }}>
            {canBuy
              ? "Purchase a plan to create a new requirement. Each plan adds jobs, resumes and AI interviews to your organisation's pool."
              : "Ask your organisation's admin to purchase a plan, then you can create a new requirement."}
          </p>
          <Link to="/app/plans?next=new" className="btn primary">
            View plans
          </Link>
        </div>
      </div>
    </section>
  );
}

function NewRequestForm(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { org } = useMyOrg();
  const { verticals } = useVerticals();

  // The org's own recent locations, newest first — teams hire for the same city again and again, so
  // the latest is pre-selected when the JD names no city. Read from the requirements already saved
  // (shared with the Requirements page cache), so it follows the team across devices.
  const { data: pastReqs } = useQuery({
    queryKey: ["requisitions"],
    queryFn: () =>
      api.get<{
        requisitions: { createdAt?: string; createdByMe?: boolean; config?: Record<string, unknown> & { location?: string } }[];
      }>("/requisitions"),
    staleTime: 60_000,
  });
  // The budget THIS user last entered — read from their own newest requirement that has one, so it
  // is per-account, follows them across devices, and changing it on a new requirement updates it.
  // Never a global default: someone who has never entered a budget gets empty fields.
  const lastBudget = useMemo<Budget | null>(() => {
    const mine = [...(pastReqs?.requisitions ?? [])]
      .filter((r) => r.createdByMe)
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
    for (const r of mine) {
      const b = budgetFromConfig(r.config ?? {}, "INR");
      if (b.min || b.max) return b;
    }
    return null;
  }, [pastReqs]);
  const recentLocations = useMemo(() => {
    const seen = new Set<string>();
    return [...(pastReqs?.requisitions ?? [])]
      .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))
      .map((r) => (r.config?.location ? placeOnly(r.config.location) : ""))
      .filter((loc) => {
        const key = loc.toLowerCase();
        if (!loc || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 4);
  }, [pastReqs]);

  // Who sources and who interviews. "We source" is the default; /app/new?mode=self pre-selects
  // "I'll source" (?mode=source still works and is simply the default now).
  const [searchParams] = useSearchParams();
  // The choice is one state shared by both steps (so it carries across Describe ⇄ Review), and is
  // remembered in this browser for the next requirement. ?mode=self / ?mode=source still win.
  const [sourcingOwner, setSourcingOwnerState] = useState<Ownership>(() => {
    const mode = searchParams.get("mode");
    if (mode === "self") return "client";
    if (mode === "source") return "us";
    try {
      const saved = localStorage.getItem(SOURCING_PREF_KEY);
      if (saved === "us" || saved === "client") return saved;
    } catch {
      // storage blocked — fall back to the default
    }
    return "us";
  });
  const setSourcingOwner = (v: Ownership) => {
    setSourcingOwnerState(v);
    try {
      localStorage.setItem(SOURCING_PREF_KEY, v);
    } catch {
      // storage blocked — the choice still holds for this page
    }
  };
  // The AI voice interview always runs the first round — the
  // "Who runs the interview?" toggle was removed from Review & save.
  const interviewOwner: Ownership = "us";
  const sourcing = sourcingOwner === "us";

  const createdIdRef = useRef<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** The last failure was a plan/credit shortfall (402) — offer the way out, not just the reason. */
  const [submitPaywall, setSubmitPaywall] = useState(false);
  /** Review & save: the "I've checked the details above" tick that unlocks Confirm. */
  const [reviewed, setReviewed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Workflow steps: "prompt" -> "review" (the chat step was removed in #236).
  const [step, setStep] = useState<"prompt" | "review">("prompt");
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const [spec, setSpec] = useState<ParsedRoleSpec>(DEFAULT_SPEC);
  const [budget, setBudget] = useState<Budget>({ currency: "INR", min: "", max: "", period: "year" });
  const [place, setPlace] = useState<{ city: string; country: string }>({ city: "", country: "India" });

  // Assessed competencies — suggested from the VERTICAL, not the prompt text. Re-seeded to that
  // vertical's full competency list (capped at the server's max of 12) whenever the vertical
  // actually changes, so switching verticals never leaves a stale, mismatched selection in place.
  const [competencies, setCompetencies] = useState<string[]>([]);
  const lastVerticalRef = useRef<string | null>(null);
  useEffect(() => {
    if (spec.verticalKey && spec.verticalKey !== lastVerticalRef.current) {
      lastVerticalRef.current = spec.verticalKey;
      const v = verticals.find((x) => x.key === spec.verticalKey);
      setCompetencies((v?.competencies ?? []).map((c) => c.competencyId).slice(0, 12));
    }
  }, [spec.verticalKey, verticals]);

  // The vertical follows Department + Industry (#237) until the recruiter picks one by hand.
  const verticalPickedByHand = useRef(false);
  const mappedFromRef = useRef<string>("");
  useEffect(() => {
    if (step !== "review" || verticalPickedByHand.current || verticals.length === 0) return;
    const keys = verticals.map((v) => v.key);
    const from = `${spec.department}|${spec.industry}`;
    if (from === mappedFromRef.current && keys.includes(spec.verticalKey)) return;
    mappedFromRef.current = from;
    const next = suggestVerticalKey({ department: spec.department, industry: spec.industry, title: spec.title }, keys);
    if (next !== spec.verticalKey) setSpec((s) => ({ ...s, verticalKey: next as ParsedRoleSpec["verticalKey"] }));
  }, [step, spec.department, spec.industry, spec.title, spec.verticalKey, verticals]);

  const [customSkillInput, setCustomSkillInput] = useState("");
  const skillChecks = useSkillChecks();
  const [summaryInstruction, setSummaryInstruction] = useState("");
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryNote, setSummaryNote] = useState<string | null>(null);

  const companyContext: CompanyContext = useMemo(
    () => ({
      name: org?.name ?? null,
      industry: org?.industry ?? null,
      region: org?.region ?? null,
      currency: org?.currency ?? null,
      website: org?.website ?? null,
    }),
    [org],
  );

  const location = joinLocation(place.city, place.country);
  /** An AI-rejected skill never reaches the screening criteria, whatever the recruiter does (#237). */
  const screeningSkills = skillChecks.approved(spec.requiredSkills);
  const skillsChecking = skillChecks.checking(spec.requiredSkills);
  const budgetIssue = budgetProblem(budget);

  const roleContext = () => ({
    title: spec.title,
    department: spec.department,
    industry: spec.industry,
    experience: spec.experience,
    location,
    workplaceMode: spec.mode,
    skills: screeningSkills.slice(0, 30),
    summary: spec.summary,
  });

  // Submit Requisition Mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const periodLabel = budget.period === "month" ? "per month" : "per year";
      const budgetFields = budgetToConfig(budget);
      const budgetMin = (budgetFields.budgetMin as string | undefined) ?? "";
      const budgetMax = (budgetFields.budgetMax as string | undefined) ?? "";
      const modeLabel = workModeLabel(spec.mode);

      // Build clean structured markdown job description
      const jobDesc = [
        spec.summary,
        "",
        `### Department: ${spec.department}`,
        `### Industry / Domain: ${spec.industry}`,
        `### Experience: ${spec.experience}`,
        `### Location & Mode: ${location} (${modeLabel})`,
        ...(budgetMin || budgetMax ? [`### Budget Range: ${[budgetMin, budgetMax].filter(Boolean).join(" - ")} ${periodLabel}`] : []),
        `### Interview Languages: ${spec.interviewLanguages.join(", ")}`,
        "",
        "### Required Screening Skills:",
        ...screeningSkills.map((s) => `- ${s}`),
      ].join("\n");

      // Payload conforming to createRequisitionSchema
      const payload = {
        title: spec.title,
        jobDescription: jobDesc,
        config: {
          entryMode: "manual_upload" as const,
          verticalKey: spec.verticalKey || "general",
          // Omit entirely rather than send []: the API's competencies field is min(1) when present
          // (packages/shared/src/schemas.ts) — an intentionally-empty selection isn't a valid one.
          ...(competencies.length > 0 ? { competencies } : {}),
          levels: extractExperience(spec.experience).levels,
          triggerHours: 24,
          applicationCap: 500,
          screenLimit: 500,
          rejectionPolicy: "manual_gated" as const,
          ...budgetFields,
          benefits: ["Remote-first", "Health insurance", "Annual bonus"],
          interviewLanguages: (spec.interviewLanguages.length > 0
            ? spec.interviewLanguages
            : ["en"]) as InterviewLanguageCode[],
          interviewOwner,
          // Dynamic calibration parameters stored as JSON in config:
          department: spec.department,
          industry: spec.industry,
          targetExperience: spec.experience,
          location,
          country: place.country || undefined,
          city: place.city || undefined,
          workplaceMode: spec.mode,
          skills: screeningSkills,
          requiredSkills: screeningSkills,
          visa: spec.visa,
          summary: spec.summary,
        },
      };

      try {
        localStorage.setItem("talenthop_last_spec", JSON.stringify(payload.config));
      } catch {
        // ignore storage errors
      }

      let id = createdIdRef.current;
      if (!id) {
        const created = await api.post<{ requisition: { id: string } }>("/requisitions", payload);
        id = created.requisition.id;
        createdIdRef.current = id;
      }
      if (sourcing) {
        // The competencies the recruiter reviewed; the vertical's own list only when none are set.
        const sourcingCompetencies =
          competencies.length > 0
            ? competencies
            : (verticals.find((v) => v.key === payload.config.verticalKey)?.competencies ?? [])
                .map((c) => c.competencyId)
                .slice(0, 12);
        await api.post(`/requisitions/${id}/sourcing`, {
          screenCount: SOURCING_SCREENS,
          interviewCount: SOURCING_INTERVIEWS,
          finalistCount: SOURCING_FINALISTS,
          competencies: sourcingCompetencies,
        });
      }
      return { requisition: { id } };
    },
    onSuccess: (res) => {
      setSubmitError(null);
      queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      queryClient.invalidateQueries({ queryKey: ["org-stats"] });
      navigate(`/app/requisitions/${res.requisition.id}`);
    },
    onError: (err) => {
      console.error("Error creating requisition:", err);
      if (sourcing) {
        // By now the requirement itself is usually saved (createdIdRef) and only the sourcing
        // request failed — say so, so pressing Confirm again doesn't look like it makes a duplicate.
        const saved = createdIdRef.current ? " The requirement itself is saved; only the sourcing request is waiting." : "";
        setSubmitPaywall(err instanceof ApiError && err.status === 402);
        setSubmitError(
          (err instanceof ApiError ? err.message : "The sourcing request could not be placed. Please try again.") + saved,
        );
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["requisitions"] });
      navigate("/app/requisitions");
    },
  });

  /**
   * A pasted JD goes to the AI (POST /requisitions/requirement/parse) for title, skills, years,
   * location… — the local keyword parse can't read one. Best-effort: unavailable/slow/failed → null,
   * and the local parse stands. One-line asks skip the call (and its cost) entirely.
   */
  async function readJobDescription(raw: string): Promise<AiParsedJd | null> {
    if (!looksLikeJobDescription(raw)) return null;
    try {
      const res = await api.post<{ available: boolean; spec?: AiParsedJd }>("/requisitions/requirement/parse", {
        text: raw.slice(0, 40000),
      });
      return res.available && res.spec ? res.spec : null;
    } catch {
      return null;
    }
  }

  /** Draft the requirement from the prompt and go straight to Review & save (#236 — no chat step). */
  function startReview(initialPromptText: string, ai: AiParsedJd | null = null) {
    const raw = initialPromptText.trim() || DEFAULT_PROMPT;
    const local = parseDynamicPrompt(raw, companyContext);
    const merged = ai ? mergeAiSpec(local, ai) : local;
    // No city in the text (and none from the AI read) → the team's most recent location, not the
    // parser's Chicago/New York/Bangalore placeholder.
    const statedLocation = Boolean(ai?.location) || namesKnownCity(raw);
    const parsed = !statedLocation && recentLocations[0] ? { ...merged, location: recentLocations[0] } : merged;
    const keys = verticals.map((v) => v.key);
    const verticalKey =
      ai?.verticalKey && keys.includes(ai.verticalKey)
        ? ai.verticalKey
        : suggestVerticalKey({ department: parsed.department, industry: parsed.industry, title: parsed.title }, keys);
    mappedFromRef.current = keys.length > 0 ? `${parsed.department}|${parsed.industry}` : "";
    verticalPickedByHand.current = false;
    setSpec({
      ...parsed,
      // A pasted / example JD stays whole as the Role summary (Markdown, editable) — the AI read's
      // 2-3 sentence summary would drop the responsibilities and requirements the interview needs.
      summary: looksLikeJobDescription(raw) ? raw.slice(0, 8000) : parsed.summary,
      experience: parsed.experience.replace(/\s*\(Job Target\)/i, ""),
      verticalKey: verticalKey as ParsedRoleSpec["verticalKey"],
    });

    const orgCountry = companyContext.currency === "INR" || companyContext.region === "in" ? "India" : "";
    const where = splitLocation(parsed.location, orgCountry || "India");
    setPlace(where);
    const currency: SalaryCurrency = parsed.budgetMin.includes("₹")
      ? "INR"
      : parsed.budgetMin.includes("$")
        ? "USD"
        : (COUNTRY_CURRENCY[where.country] ?? "INR");
    const stated = amountOf(parsed.budgetMin) || amountOf(parsed.budgetMax);
    setBudget(
      stated
        ? { currency, min: amountOf(parsed.budgetMin), max: amountOf(parsed.budgetMax), period: "year" }
        : (lastBudget ?? { currency, min: "", max: "", period: "year" }),
    );

    skillChecks.reset();
    setSummaryInstruction("");
    setSummaryNote(null);
    setSubmitError(null);
    setSubmitPaywall(false);
    setReviewed(false);
    setPrompt(raw);
    setStep("review");
  }

  async function handlePromptSubmit() {
    setIsGenerating(true);
    // A one-line ask parses locally in a blink; hold the progress bar long enough to be seen so the
    // click visibly did something before the page swaps to Review & save.
    const [ai] = await Promise.all([readJobDescription(prompt.trim()), new Promise((r) => setTimeout(r, 600))]);
    setIsGenerating(false);
    startReview(prompt, ai);
  }

  /** An example only fills the box — the recruiter can edit it, then presses Start themselves. */
  const promptRef = useRef<HTMLDivElement>(null);
  function applyExample(text: string) {
    setPrompt(text);
    // The editor's own textarea (absent while its Preview tab is showing — then just scroll to it).
    const el = promptRef.current?.querySelector("textarea");
    if (!el) {
      promptRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
    // A full JD opens at its first line, so the title and "About the company" are what you see.
    requestAnimationFrame(() => {
      el.setSelectionRange(0, 0);
      el.scrollTop = 0;
    });
  }

  function handleFileUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");

    if (file.type.startsWith("text/") || file.name.endsWith(".txt")) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = String(ev.target?.result || "");
        if (content.trim()) {
          setPrompt(content.slice(0, 1200));
        } else {
          setPrompt(`Job Specification for ${cleanName} (from ${file.name})`);
        }
      };
      reader.readAsText(file);
    } else {
      setPrompt(`Job Specification for ${cleanName} (from ${file.name})`);
    }
  }

  function handleRemoveSkill(skill: string) {
    setSpec((prev) => ({ ...prev, requiredSkills: prev.requiredSkills.filter((s) => s !== skill) }));
    skillChecks.forget(skill);
  }

  /**
   * Add a skill, then ask the AI whether it belongs to this role (#237). Relevant → it stays as an
   * ordinary skill. Irrelevant → it stays visible as a red tag that will NOT be saved. Check
   * unavailable → it is added, marked "not checked" (degrade, never block).
   */
  function handleAddCustomSkill() {
    const skill = customSkillInput.trim().replace(/\s+/g, " ").slice(0, 80);
    if (!skill) return;
    setCustomSkillInput("");
    if (spec.requiredSkills.some((s) => s.toLowerCase() === skill.toLowerCase())) return;
    setSpec((prev) => ({ ...prev, requiredSkills: [...prev.requiredSkills, skill] }));
    void skillChecks.check(skill, roleContext());
  }

  async function handleRegenerateSummary() {
    setSummaryBusy(true);
    setSummaryNote(null);
    try {
      const res = await api.post<{ available: boolean; summary?: string }>("/requisitions/requirement/summary", {
        role: roleContext(),
        instruction: summaryInstruction.trim(),
      });
      if (res.available && res.summary) {
        setSpec((s) => ({ ...s, summary: res.summary! }));
        setSummaryInstruction("");
      } else {
        setSummaryNote("The AI rewrite is unavailable right now — you can still edit the summary directly.");
      }
    } catch {
      setSummaryNote("The AI rewrite is unavailable right now — you can still edit the summary directly.");
    } finally {
      setSummaryBusy(false);
    }
  }

  function handleToggleInterviewLanguage(code: string) {
    setSpec((prev) => {
      if (code === "en") return prev; // English is locked as base
      const exists = prev.interviewLanguages.includes(code);
      const updated = exists
        ? prev.interviewLanguages.filter((c) => c !== code)
        : [...prev.interviewLanguages, code];
      return { ...prev, interviewLanguages: updated };
    });
  }

  // Generate inbound email address slug based on title
  const inboundSlug = useMemo(() => {
    return (
      "jobs+" +
      spec.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 32) +
      "@talenthop.in"
    );
  }, [spec.title]);

  const selectedVertical = verticals.find((v) => v.key === spec.verticalKey);
  const confirmBlocked = createMutation.isPending || skillsChecking || Boolean(budgetIssue) || spec.title.trim().length < 2;

  return (
    <div className="ai-spec-container t1n">
      {/* Page header (X1) — the org is already in the top bar, so no org pill / emoji here */}
      <header className="page-h t1n-head">
        <div>
          {sourcing && <span className="pill t t1n-eyebrow">We source</span>}
          <h1>New requirement</h1>
          <div className="sub">
            {sourcing
              ? `Give us the requirement. We screen ${SOURCING_SCREENS}+ resumes, interview the top ${SOURCING_INTERVIEWS}+, and recommend ${SOURCING_FINALISTS} finalists.`
              : "Describe the role in plain words. AI drafts the screening criteria and interview plan; you review everything before it is saved."}
          </div>
        </div>

        <div className="t1n-head-actions">
          {step === "review" && (
            <button type="button" className="btn" onClick={() => setStep("prompt")}>
              <RefreshCw />
              <span>Start over</span>
            </button>
          )}
          <Link to="/app/requisitions" className="btn">
            Cancel
          </Link>
        </div>
      </header>

      {/* 2-step progress indicator — plain text, never pill-shaped, so it doesn't read as buttons */}
      <div className="spec-stepper" aria-label="Progress">
        <div
          className={`spec-step-item static ${step === "review" ? "completed" : "active"}`}
          aria-current={step === "prompt" ? "step" : undefined}
        >
          <span className="spec-step-num">{step === "review" ? "✓" : "1"}</span>
          <span>Describe the role</span>
        </div>
        <div className="spec-step-divider" />
        <div
          className={`spec-step-item static ${step === "review" ? "active" : ""}`}
          aria-current={step === "review" ? "step" : undefined}
        >
          <span className="spec-step-num">2</span>
          <span>Review &amp; save</span>
        </div>
      </div>

      {/* STEP 1: PROMPT */}
      {step === "prompt" && (
        <div className="spec-prompt-wrapper">
          <div className="spec-prompt-card">
            <div className="spec-prompt-header">
              <div className="spec-icon-badge">
                <Sparkles style={{ width: 20, height: 20 }} />
              </div>
              <div>
                <h3 className="spec-prompt-title">What requirement do you want to hire for?</h3>
                <p className="spec-prompt-desc">
                  Type naturally (e.g. &ldquo;I need a Frontend Developer for our company&rdquo;),
                  paste a prompt, or upload an existing spec.
                </p>
              </div>
            </div>

            <OwnershipToggle
              label="Who sources the candidates?"
              value={sourcingOwner}
              onChange={setSourcingOwner}
              options={SOURCING_OPTIONS}
            />

            {/* Same Write / Preview Markdown editor as Review & save and the Edit form, so a title,
                ### headings and - bullets can be typed or pasted here and carry straight through. */}
            <div className="spec-textarea-container nr-md-prompt" ref={promptRef}>
              <MarkdownEditor
                id="nr-prompt"
                value={prompt}
                onChange={setPrompt}
                rows={looksLikeJobDescription(prompt) ? 14 : 5}
                showTidy={false}
                placeholder={
                  companyContext.name
                    ? `e.g. I need a Frontend Developer for ${companyContext.name} with 3+ years experience — or paste a full job description (title, about the company, responsibilities, requirements).`
                    : DEFAULT_PROMPT
                }
              />

              <div className="spec-textarea-toolbar">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.txt"
                />
                <button
                  type="button"
                  className="btn-upload-spec-doc"
                  onClick={() => fileInputRef.current?.click()}
                  data-tip="PDF, Word or text"
                  data-tip-pos="top"
                >
                  <UploadCloud style={{ width: 15, height: 15 }} />
                  <span>Upload a spec</span>
                </button>

                <button
                  type="button"
                  className="btn-generate-spec"
                  onClick={handlePromptSubmit}
                  disabled={isGenerating}
                  aria-busy={isGenerating}
                >
                  {isGenerating && <span className="nr-btn-spinner" aria-hidden="true" />}
                  <span>{isGenerating ? (looksLikeJobDescription(prompt) ? "Reading your job description…" : "Drafting…") : "Start →"}</span>
                </button>
              </div>
              {isGenerating && (
                <div className="nr-progress" role="progressbar" aria-label="Drafting the requirement">
                  <span />
                </div>
              )}
            </div>
          </div>

          {/* Suggested Prompts Section */}
          <div className="suggested-prompts-box">
            <div className="suggested-prompts-label">Or start from an example (it fills the box above, then press Start)</div>
            <div className="suggested-chips-list">
              {PRESET_PROMPTS.map((p) => {
                const text = p.text(companyContext.name ?? "Our company");
                const fullJd = text !== p.label;
                return (
                  <button
                    key={p.label}
                    type="button"
                    className="suggested-chip"
                    title={fullJd ? `${p.label} — fills a full job description` : p.label}
                    onClick={() => applyExample(text)}
                  >
                    <Sparkles className="chip-icon" />
                    <span>
                      {p.label}
                      {fullJd && <em className="nr-chip-tag">Full job description</em>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: REVIEW & SAVE */}
      {step === "review" && (
        <div className="spec-review-wrapper">
          <div className="calibration-card">
            <div className="spec-review-header">
              <div className="spec-review-title-box">
                <div className="spec-icon-badge" style={{ background: "var(--teal-wash)" }}>
                  <Briefcase style={{ width: 18, height: 18, color: "var(--teal)" }} />
                </div>
                <div>
                  <h3 className="spec-prompt-title" style={{ fontSize: 16 }}>
                    Job Specification Review
                  </h3>
                  <p className="spec-prompt-desc">
                    Drafted by AI from your description &amp; company context — edit anything before saving
                  </p>
                </div>
              </div>
              <span className="draft-preview-badge">Draft Review</span>
            </div>

            <div className="spec-review-content">
              {/* Job Title, Department, Industry, Experience */}
              <div className="spec-fields-2col" style={{ marginBottom: 14 }}>
                <div>
                  <label className="field-label-bold">JOB TITLE</label>
                  <input
                    type="text"
                    className="text-input-field"
                    value={spec.title}
                    onChange={(e) => setSpec((s) => ({ ...s, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="field-label-bold">DEPARTMENT</label>
                  <SearchableSelect
                    options={DEPARTMENT_OPTIONS}
                    value={spec.department}
                    onChange={(val) => setSpec((s) => ({ ...s, department: val }))}
                    placeholder="Select or type department…"
                  />
                </div>
              </div>

              <div className="spec-fields-2col" style={{ marginBottom: 14 }}>
                <div>
                  <label className="field-label-bold">INDUSTRY / DOMAIN</label>
                  <SearchableSelect
                    options={INDUSTRY_OPTIONS}
                    value={spec.industry}
                    onChange={(val) => setSpec((s) => ({ ...s, industry: val }))}
                    placeholder="Select or type industry…"
                  />
                </div>
                <div>
                  <label className="field-label-bold" htmlFor="nr-experience">EXPERIENCE</label>
                  <ExperienceInput
                    id="nr-experience"
                    value={spec.experience}
                    onChange={(v) => setSpec((s) => ({ ...s, experience: v }))}
                  />
                </div>
              </div>

              {/* Budget — currency, amount, period */}
              <label className="field-label-bold">
                BUDGET RANGE <span className="mut" style={{ fontWeight: 500, textTransform: "none" }}>(optional)</span>
              </label>
              <BudgetFields budget={budget} onChange={setBudget} />

              {/* Location & Mode — country, city, work mode */}
              <label className="field-label-bold">LOCATION &amp; MODE</label>
              <LocationFields
                idPrefix="nr"
                place={place}
                onPlaceChange={(next, countryChanged) => {
                  setPlace(next);
                  // A new country suggests its currency only while no amount has been typed.
                  const cur = COUNTRY_CURRENCY[next.country];
                  if (countryChanged && !budget.min && !budget.max && cur) setBudget((b) => ({ ...b, currency: cur }));
                }}
                mode={spec.mode}
                onModeChange={(mode) => setSpec((s) => ({ ...s, mode }))}
                recentLocations={recentLocations}
              />

              {/* Assessment Vertical — chosen from Department + Industry; shown so the team can see
                  what was picked until the mapping is trusted enough to hide it (#237). */}
              <label className="field-label-bold">ASSESSMENT VERTICAL</label>
              <Dropdown
                ariaLabel="Assessment vertical"
                className="nr-dd-block"
                value={spec.verticalKey}
                options={verticals.map((v) => ({ value: v.key, label: v.name }))}
                onChange={(key) => {
                  verticalPickedByHand.current = true;
                  setSpec((s) => ({ ...s, verticalKey: key as ParsedRoleSpec["verticalKey"] }));
                }}
              />
              <p className="mut sm nr-hint">
                {verticalPickedByHand.current
                  ? "Picked by you."
                  : "Picked automatically from the department and industry — it follows them when they change."}
              </p>

              {/* Competencies Assessed — suggested from the vertical above, editable before creation */}
              <label className="field-label-bold">
                COMPETENCIES ASSESSED ({competencies.length})
              </label>
              <p className="mut sm" style={{ margin: "0 0 8px" }}>
                Suggested from the {selectedVertical?.name ?? "selected"} vertical — remove any with ×, or
                use + Add competency for more.
              </p>
              <CompetencyPicker variant="add" vertical={selectedVertical} selected={competencies} onChange={setCompetencies} />

              {/* Role Summary & Scope — editable, or rewritten by AI from a prompt */}
              {/* Same Markdown editor (Write / Preview, toolbar) as the requirement's Edit form */}
              <label className="field-label-bold" htmlFor="nr-role-summary">ROLE SUMMARY &amp; SCOPE</label>
              <MarkdownEditor
                id="nr-role-summary"
                value={spec.summary}
                onChange={(next) => setSpec((s) => ({ ...s, summary: next }))}
                rows={looksLikeJobDescription(spec.summary) ? 14 : 5}
                showTidy={false}
                placeholder="Describe the role — use ### headings and - bullets for responsibilities and requirements."
              />
              <div className="chat-skill-input-row" style={{ marginBottom: summaryNote ? 4 : 14 }}>
                <input
                  type="text"
                  className="chat-mini-input"
                  placeholder="Regenerate with a prompt (e.g. Make it shorter, focus on payments experience)…"
                  value={summaryInstruction}
                  maxLength={600}
                  onChange={(e) => setSummaryInstruction(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !summaryBusy && handleRegenerateSummary()}
                />
                <button type="button" className="chat-mini-btn" onClick={handleRegenerateSummary} disabled={summaryBusy}>
                  <Sparkles style={{ width: 12, height: 12 }} />
                  <span>{summaryBusy ? "Regenerating…" : "Regenerate"}</span>
                </button>
              </div>
              {summaryNote && <p className="mut sm nr-hint nr-hint-error">{summaryNote}</p>}

              {/* Required Screening Skills — an added skill is checked for relevance by AI */}
              <label className="field-label-bold">
                REQUIRED SCREENING SKILLS ({screeningSkills.length})
              </label>
              <CheckedSkillChips skills={spec.requiredSkills} checks={skillChecks.checks} onRemove={handleRemoveSkill} />
              <div className="chat-skill-input-row" style={{ marginBottom: 14 }}>
                <input
                  type="text"
                  className="chat-mini-input"
                  placeholder="Add a skill (e.g. Chart of Accounts, Power BI)…"
                  value={customSkillInput}
                  maxLength={80}
                  onChange={(e) => setCustomSkillInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustomSkill()}
                />
                <button type="button" className="chat-mini-btn" onClick={handleAddCustomSkill}>
                  Add
                </button>
              </div>

              {/* Voice Interview Languages */}
              <label className="field-label-bold">
                VOICE AI INTERVIEW LANGUAGES ({spec.interviewLanguages.length})
              </label>
              <div className="chat-languages-grid" style={{ marginBottom: 14 }}>
                {Object.entries(INTERVIEW_LANGUAGES).map(([code, lang]) => {
                  const isSelected = spec.interviewLanguages.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      className={`chat-lang-pill ${isSelected ? "selected" : ""}`}
                      onClick={() => handleToggleInterviewLanguage(code)}
                      title={code === "en" ? "English is required as the default baseline" : `Toggle ${lang.label}`}
                    >
                      <span className="lang-check">{isSelected ? "✓" : "+"}</span>
                      <span className="lang-label">{lang.label}</span>
                      <span className="lang-native">({lang.nativeLabel})</span>
                    </button>
                  );
                })}
              </div>

              {/* Screening & Ingestion Criteria Box */}
              <label className="field-label-bold">SCREENING &amp; INGESTION CRITERIA</label>
              <div className="spec-ingestion-criteria-box" style={{ marginBottom: 14 }}>
                <div>
                  • Visa Sponsorship: <b>{spec.visa}</b>
                </div>
                <div style={{ wordBreak: "break-all", marginTop: 4 }}>
                  • System Inbound Address: <code>{inboundSlug}</code>
                </div>
              </div>

            </div>

            {/* Bottom Confirmation Banner */}
            <div className="spec-confirm-banner">
              <div className="confirm-info-box">
                <div className="nr-confirm-body">
                  {/* Same choice as step 1 (one shared state) — right before the review tick box. */}
                  <OwnershipToggle
                    label="Who sources the candidates?"
                    value={sourcingOwner}
                    onChange={(v) => {
                      setSourcingOwner(v);
                      setSubmitError(null);
                      setSubmitPaywall(false);
                    }}
                    options={SOURCING_OPTIONS}
                  />
                  <label className="nr-confirm-check">
                    <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />
                    <span>I&apos;ve checked the details above</span>
                  </label>
                  {/* Confirm sits under the tick box it depends on */}
                  <button
                    type="button"
                    className="btn-confirm-and-save"
                    onClick={() => createMutation.mutate()}
                    disabled={confirmBlocked || !reviewed}
                    data-tip={!reviewed ? "Tick “I've checked the details above” first" : undefined}
                    data-tip-pos="top"
                  >
                    <span>
                      {createMutation.isPending
                        ? sourcing
                          ? "Placing sourcing request..."
                          : "Saving Requisition..."
                        : sourcing
                          ? `Confirm & Request Sourcing →`
                          : "Confirm & Save Request →"}
                    </span>
                  </button>
                  {skillsChecking && <div className="confirm-sub">Checking the skills you added…</div>}
                  {submitError && (
                    <div className="confirm-error" role="alert">
                      {submitError}
                      {submitPaywall && (
                        <div className="confirm-error-actions">
                          <Link to="/app/plans" className="btn sm">
                            Buy a plan
                          </Link>
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => {
                              setSourcingOwner("client");
                              setSubmitError(null);
                              setSubmitPaywall(false);
                            }}
                          >
                            Switch to I&apos;ll source
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
